import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User, Users, Plus, Settings2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useTenants } from '@/hooks/useTenants';
import { roleLabel } from '@/utils/tenant';
import { InlinePrompt } from '@/components/common/InlinePrompt';

// 工作区切换器（侧边栏最顶部，脑切换器上方）：
// 个人空间 ⇄ 各团队空间；团队空间是第三作用域。
// 游客演示也渲染（只读浏览；切换走游客中间件白名单的共享 demo 态翻转），
// 隐藏创建/成员管理等写入口
const WorkspaceSwitcher: FC = () => {
  const { isLoggedIn } = useAuth();
  const { tenants, activeTenant, createTenant, activateTenant, deactivateTenant, isSwitching } = useTenants();
  const [open, setOpen] = useState(false);
  // 内联创建团队空间（Electron 不支持 window.prompt/alert，统一走受控内联输入行）
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  // 切换失败的内联错误条（下拉顶部）
  const [switchError, setSwitchError] = useState<string | null>(null);
  const navigate = useNavigate();

  // 游客且演示号没有任何团队：切换器没有可展示的内容，保持隐藏
  if (!isLoggedIn && !(tenants || []).length) return null;

  const toggleOpen = () => {
    setOpen(!open);
    // 收起下拉时清掉上次的输入与错误残留
    setCreating(false);
    setCreateError(null);
    setSwitchError(null);
  };

  const handleSwitch = async (tenantId: string | null) => {
    setSwitchError(null);
    try {
      if (tenantId) await activateTenant(tenantId);
      else await deactivateTenant();
      setOpen(false);
    } catch (err: any) {
      setSwitchError(err?.response?.data?.error?.message || err?.response?.data?.detail || err.message || '切换失败');
    }
  };

  const handleCreateSubmit = async (name: string) => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      await createTenant(name);
      setCreating(false);
      setOpen(false);
    } catch (err: any) {
      setCreateError(err?.response?.data?.error?.message || err?.response?.data?.detail || err.message || '创建失败');
    } finally {
      setCreateBusy(false);
    }
  };

  return (
    <div className="relative mb-2">
      {/* 抬头：当前工作区（点击展开切换下拉） */}
      <button
        onClick={toggleOpen}
        disabled={isSwitching}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-[2px] border transition-colors duration-200 ${
          activeTenant
            ? 'border-info/30 bg-info/10'
            : 'border-border-color bg-white/[0.03]'
        } hover:border-info/40 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isSwitching ? (
          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : activeTenant ? (
          <Users className="w-5 h-5 text-info" />
        ) : (
          <User className="w-5 h-5 text-text-secondary" />
        )}
        <div className="flex-1 text-left min-w-0">
          <div className={`text-xs font-semibold truncate ${activeTenant ? 'text-info' : 'text-text-primary'}`}>
            {activeTenant ? activeTenant.name : '个人空间'}
          </div>
          <div className="text-[10px] text-text-muted">
            {activeTenant ? `${roleLabel(activeTenant.my_role)} · ${activeTenant.member_count}/${activeTenant.max_users} 席` : '仅我可见'}
          </div>
        </div>
        <motion.svg
          width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className="text-text-muted"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 right-0 mt-2 glass rounded-[2px] border border-border-color p-2 z-50 max-h-[60vh] overflow-y-auto"
          >
            {/* 切换失败的内联错误条 */}
            {switchError && (
              <div className="mb-1.5 px-3 py-1.5 rounded-[2px] text-[11px] text-danger bg-danger/10 border border-danger/20 leading-snug">
                {switchError}
              </div>
            )}
            {/* 个人空间固定第一项 */}
            <button
              onClick={() => handleSwitch(null)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-[2px] text-sm transition-colors ${
                !activeTenant
                  ? 'bg-white/[0.05] text-text-primary border border-white/[0.1]'
                  : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
              }`}
            >
              <div className="w-8 h-8 rounded-[2px] bg-white/[0.05] flex items-center justify-center">
                <User className="w-4 h-4 text-text-secondary" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-sm font-medium">个人空间</div>
                <div className="text-[10px] text-text-muted">仅我可见</div>
              </div>
              {!activeTenant && <div className="w-2 h-2 rounded-full bg-info" />}
            </button>

            {(tenants || []).length > 0 && (
              <div className="px-3 pt-1.5 pb-1 text-[10px] text-text-muted flex items-center gap-1.5">
                <Users className="w-3 h-3" />
                团队空间
              </div>
            )}
            {(tenants || []).map((t) => (
              <div key={t.id} className="flex items-center gap-1">
                <button
                  onClick={() => handleSwitch(t.id)}
                  className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-[2px] text-sm transition-colors ${
                    activeTenant?.id === t.id
                      ? 'bg-info/10 text-info border border-info/25'
                      : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary'
                  }`}
                >
                  <div className="w-8 h-8 rounded-[2px] bg-info/10 flex items-center justify-center">
                    <Users className="w-4 h-4 text-info" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-[10px] text-text-muted">
                      {roleLabel(t.my_role)} · {t.member_count}/{t.max_users} 席
                    </div>
                  </div>
                  {activeTenant?.id === t.id && <div className="w-2 h-2 rounded-full bg-info" />}
                </button>
                {/* 管理成员入口只在当前激活的团队项上显示（游客隐藏） */}
                {activeTenant?.id === t.id && isLoggedIn && (
                  <button
                    onClick={() => { setOpen(false); navigate('/tenant/members'); }}
                    className="p-2 rounded-[2px] text-text-muted hover:text-info hover:bg-white/[0.05] transition-colors"
                    title="管理成员"
                  >
                    <Settings2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}

            {isLoggedIn && (
              <>
                <div className="my-1.5 border-t border-border-color" />
                {creating ? (
                  <div className="px-1 py-1">
                    <InlinePrompt
                      placeholder="团队空间名称"
                      submitText="创建"
                      error={createError}
                      busy={createBusy}
                      onSubmit={handleCreateSubmit}
                      onCancel={() => { setCreating(false); setCreateError(null); }}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => { setCreating(true); setCreateError(null); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-[2px] text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors"
                  >
                    <div className="w-8 h-8 rounded-[2px] border border-dashed border-border-color flex items-center justify-center">
                      <Plus className="w-4 h-4" />
                    </div>
                    创建团队空间
                  </button>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkspaceSwitcher;
